import { useState, type MouseEventHandler } from 'react';
import { AiOutlineZoomIn } from 'react-icons/ai';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import numberHumanizer from '@/helpers/numberHumanizer';
import {
  normalizeUriIssueKey,
  uriIssueLabel
} from '@/app/Crawl/config/uriIssueConfig';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';

type UriIssueRendererProps = {
  data?: unknown;
  onClick?: MouseEventHandler<SVGElement>;
};

const issueTooltip = (key: string) =>
  crawlTooltip.uriIssueTypes[normalizeUriIssueKey(key)];

const UriIssueLabel = ({ issueKey }: { issueKey: string }) => {
  const key = normalizeUriIssueKey(issueKey);
  const label = uriIssueLabel(key);
  const tip = issueTooltip(key);

  if (!tip) {
    return <span className="text-truncate d-inline-block">{label}</span>;
  }

  return (
    <OverlayTrigger
      placement="auto"
      overlay={
        <Tooltip className="text-start" style={{ maxWidth: 320 }}>
          {tip}
        </Tooltip>
      }
    >
      <span
        className="text-truncate d-inline-block cursor-help text-800"
        style={{ borderBottom: '1px dotted currentColor' }}
      >
        {label}
      </span>
    </OverlayTrigger>
  );
};

const UriIssueRenderer = ({ data, onClick }: UriIssueRendererProps) => {
  const [opened, setOpened] = useState(false);
  const values = Array.isArray(data)
    ? data.map(v => normalizeUriIssueKey(v)).filter(Boolean)
    : [];

  if (!values.length) {
    return null;
  }

  if (values.length > 1) {
    const summary = values.map(k => uriIssueLabel(k)).join('، ');
    return (
      <div className="d-flex align-items-center gap-1">
        <span
          onClick={() => {
            setOpened(true);
            setTimeout(() => setOpened(false), 1500);
          }}
        >
          {!opened ? (
            <AiOutlineZoomIn
              onClick={onClick}
              className="cursor-pointer fs-0"
            />
          ) : (
            <Spinner
              animation="border"
              style={{
                width: '18px',
                marginBottom: '-3px',
                paddingLeft: '3px',
                height: '18px'
              }}
            />
          )}
        </span>
        <OverlayTrigger
          placement="auto"
          overlay={
            <Tooltip className="text-start" style={{ maxWidth: 360 }}>
              <div className="fw-bold mb-1">مسائل نشانی:</div>
              <ul className="mb-0 ps-3 text-start">
                {values.map(key => (
                  <li key={key}>
                    <span className="fw-semibold">{uriIssueLabel(key)}</span>
                    {issueTooltip(key) ? (
                      <span className="d-block fs--1 opacity-75">
                        {issueTooltip(key)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Tooltip>
          }
        >
          <span className="cursor-help" title={summary}>
            {numberHumanizer(values.length)}
          </span>
        </OverlayTrigger>
      </div>
    );
  }

  return <UriIssueLabel issueKey={values[0]} />;
};

export default UriIssueRenderer;
